import { useRef } from 'react';
import { addListener, accept } from '../utils/event.js';
import { exitPointerLock, USE_POINTER_LOCK, requestPointerLock, hasPointerLock } from '../utils/pointer-lock.js';
import { ZERO, fromMovementXY, add } from '../utils/vector.js';
import { useCancellableDrag } from './use-drag/use-drag.js';
import 'clsx';
/**
 * Allows reading infinite relative mouse movement, unencumbered by the edges of the screen.
 *
 * Works by using [pointer-lock] to capture the cursor and then reading `movementX` and `movementY`.
 *
 * If the browser does not support pointer lock, or if using pointer lock would be
 * intrusive (like on Safari), then this hook falls back to pointerCapture but reads
 * from the event's `movementX` and `movementY` values. If `movementX` and `movementY`
 * are not supported (like on iPad), then calculates the delta based on the mouse down
 * start position and the current mouse position.
 *
 * | Browser    | Pointer Lock | Infinite Movement |
 * | ---------- | ------------ | ----------------- |
 * | Chrome     | ✅           | ✅                |
 * | Firefox    | ✅           | ✅                |
 * | Safari     | ❌           | ✅*               |
 * | Safari iOS | ❌           | ❌                |
 *
 * \* Safari reports movement values beyond the edge of the screen.
 *
 * [pointer-lock]: https://developer.mozilla.org/en-US/docs/Web/API/Pointer_Lock_API
 */
function useInfiniteDrag({ onPointerLockPermissionChange, ...ctx }) {
    const delta = useRef(ZERO);
    const pointerLockChangeCleanup = useRef();
    const escapeCleanup = useRef();
    return useCancellableDrag({
        ...ctx,
        onDragStart(e, { registerAbortSignal }) {
            const controller = new AbortController();
            registerAbortSignal(controller.signal);
            function addEscapeListener() {
                escapeCleanup.current?.();
                escapeCleanup.current = addListener(document, 'keydown', (keydownEvt) => {
                    if (keydownEvt.key === 'Escape') {
                        controller.abort();
                        accept(keydownEvt);
                    }
                }, true);
            }
            if (USE_POINTER_LOCK) {
                const el = e.currentTarget;
                requestPointerLock(el)
                    .then(() => {
                    onPointerLockPermissionChange?.(2 /* PointerLockPermission.ACCEPTED */);
                    pointerLockChangeCleanup.current?.();
                    pointerLockChangeCleanup.current = addListener(document, 'pointerlockchange', () => {
                        if (!hasPointerLock(el))
                            controller.abort();
                    });
                })
                    .catch(() => {
                    onPointerLockPermissionChange?.(0 /* PointerLockPermission.DENIED */);
                    addEscapeListener();
                });
            }
            else {
                addEscapeListener();
            }
            delta.current = ZERO;
            ctx.onDragStart?.(e);
        },
        onDrag(e) {
            const movement = fromMovementXY(e);
            delta.current = add(delta.current, movement);
            ctx.onDrag(Object.assign(e, { delta: delta.current, movement }));
        },
        onDragEnd(e) {
            pointerLockChangeCleanup.current?.();
            pointerLockChangeCleanup.current = undefined;
            escapeCleanup.current?.();
            escapeCleanup.current = undefined;
            exitPointerLock();
            if (e.cancelled) {
                ctx.onDragEnd?.({ cancelled: true });
                return;
            }
            ctx.onDragEnd?.(Object.assign(e, { delta: delta.current, movement: ZERO }));
        },
    });
}

export { useInfiniteDrag };
//# sourceMappingURL=use-infinite-drag.js.map
